import { ChangeDetectorRef, Component, HostListener, Inject, OnDestroy, OnInit, Renderer2 } from "@angular/core";
import { filter, map, tap } from "rxjs/operators";
import { SubSink } from "subsink";
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, Router } from "@angular/router";
import { BaseJWTService } from "projects/core/app/services/abstracts/jwt.service";
import { NavigationService } from "projects/core/app/services/navigation.service";
import { OverlayService } from "projects/core/app/services/overlay.service";
import { DOCUMENT } from "@angular/common";
import dayjs from "dayjs";
import { timer } from "rxjs";
import { BaseSessionService } from "projects/core/app/services/abstracts/session.service";
import { CacheService } from "projects/core/app/services/cache.service";
import { NotificationService, NotificationInstance, NotificationTypes } from "projects/core/app/services/notification.service";
import { PushUpdatesService } from "projects/core/app/services/push-updates.service";
import { FAST_ANIMATION_DURATION } from "projects/core/app/utils/animations";

@Component({
  selector: "app-base",
  standalone: true,
  template: "",
})
export abstract class BaseAppComponent implements OnInit, OnDestroy {
  public title = "app";
  private _subs = new SubSink();
  public loading = true;
  public appView = false;
  public hideTopNav = false;
  public isOverlayOpen: boolean;
  public isOverlayCloseable = true;

  @HostListener("document:keydown.escape", ["$event"]) onKeydownHandler() {
    this.closeOverlay();
  }

  constructor(
    protected _router: Router,
    protected _jwtService: BaseJWTService,
    protected _activatedRoute: ActivatedRoute,
    protected _overlayService: OverlayService,
    protected _changeDetectorRef: ChangeDetectorRef,
    protected _renderer: Renderer2,
    protected _pushUpdatesService: PushUpdatesService,
    protected _notificationService: NotificationService,
    protected _navigationService: NavigationService,
    protected _cacheService: CacheService,
    protected _sessionService: BaseSessionService,
    @Inject(DOCUMENT) protected _document: Document
  ) {
    this._subs.sink = this._overlayService.onOverlayVisibilityChange.subscribe((is_open) => {
      this.isOverlayOpen = is_open;
      this._changeDetectorRef.detectChanges();
      const method = is_open ? "addClass" : "removeClass";
      this._renderer[method](this._document.body, "overflow-hidden");
    });
    this._subs.sink = this._overlayService.onOverlayCloseableChange.subscribe((isOverlayCloseable) => {
      this.isOverlayCloseable = isOverlayCloseable;
    });

    this._subs.sink = this._router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        tap((event: NavigationEnd) => {
          const isMyDental = event.url.includes("/my-dental");
          const isPublic = this._jwtService.isPublicAccess();
          if (isMyDental && isPublic) {
            this._navigationService.navigate("/");
            return;
          }

          this.loading = true;
          // Manual url tracking for HotJar
          if (window.hj) window.hj("stateChange", event.url);
          if (window.ga) {
            (<any>window).ga("set", "page", event.urlAfterRedirects);
            (<any>window).ga("send", "pageview");
            if (window.clientTracker) {
              (<any>window).ga("clientTracker.set", "page", event.urlAfterRedirects);
              (<any>window).ga("clientTracker.send", "pageview");
            }
          }

          try {
            setTimeout(() => {
              document.body.scrollTop = 0;
              window.scrollTo(0, 0);
            }, FAST_ANIMATION_DURATION); // Delay to match the animation duration of the router outlet
          } catch (err) {
            return err;
          }
        }),
        map(() => this._activatedRoute.snapshot)
      )
      .subscribe((route: ActivatedRouteSnapshot) => {
        this._handleRouteData(route);
        return route;
      });

    this._handleRouteData(this._activatedRoute.snapshot);
  }

  private _handleRouteData(route: ActivatedRouteSnapshot) {
    if (!route) return;

    while (route.firstChild) {
      route = route.firstChild;
      this.appView = !!route.data.appView;
      this.hideTopNav = !!route.data.hideTopNav;
    }
    this.loading = false;
  }

  ngOnInit() {
    this._connectToPusher();
    this._bindEvents();
  }

  private _connectToPusher() {
    this._pushUpdatesService.connect(this._jwtService.getJWTString());
  }

  private _bindEvents() {
    this._subs.sink = this._pushUpdatesService.downForMaintenanceSubject.pipe(filter((data) => data.status === "down")).subscribe((data) => {
      this._notificationService.open(
        new NotificationInstance({
          title: "Maintenance advance notice",
          body: `Portal will go down for maintenance at ${dayjs()
            .add(data.delay / 1000, "seconds")
            .format("h:mm a")}`,
          type: NotificationTypes.INFO,
          timeout: data.delay,
        })
      );

      this._subs.sink = timer(data.delay).subscribe(() => {
        window.location.reload();
      });
    });

    this._subs.sink = this._pushUpdatesService.onDisconnected.subscribe(() => {
      this._connectToPusher();
    });
  }

  ngOnDestroy() {
    this._subs.unsubscribe();
  }

  public closeOverlay(): void {
    if (!this.isOverlayCloseable || !this.isOverlayOpen) return;

    this._overlayService.close();
  }
}
